import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest, apiFormRequest, awardArt } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';
import './HostEvent.css';
// The shelf is a row of chips, and chips are the event-tag ones — same size, same behaviour, so a
// library of prizes reads like every other "pick one of these" row in the app.
import '../../components/common/EventTags.css';

/* The prizes at the end of the night: what is handed out, and to whom.

   ★★ **The screen is a RUNNING ORDER, not a list.** The prizes are in the order they will be
   given, the winner sits on the row, and the last control on each row is the one that reveals it.
   That is the shape of the five minutes this screen exists for: a person with a microphone going
   down a list, one row at a time. Everything that is preparation — inventing the prize, giving it
   a picture, writing the line the room will read — is above or behind an expander, because it is
   done in the afternoon and never during.

   ★★ **Nothing here reaches a participant until it is REVEALED.** A winner can be filled in hours
   early and the app shows "to be announced" until the host presses reveal, which is also the only
   thing that sends a notification. The same promise the projector board makes about the standing;
   the server enforces it and this screen only draws it.

   ★ **A prize goes to a team, to a person or to a company.** Which picker a row draws is the
   AWARD's kind — chosen when it is invented and changeable — and the three candidate lists come
   from the payload: the teams on the day, the people who were in the room, the companies with a
   team here plus the organiser. Nothing is typed; everything is picked, because the server refuses
   any winner that was not on one of those three lists.

   ★ **Inventing and adding are one press.** Typing a name adds the prize to tonight AND puts it on
   the company's shelf, so the same award is one tap next quarter. The shelf is the strip of chips
   under the form. A Lab has no company and therefore no shelf — the payload says so and the form
   is left out rather than drawn to always refuse. */

const KIND_ICONS = { team: 'users', person: 'user', company: 'building' };

/* An empty draft. `kind` opens on a team because that is what most prizes are, and because a
   company day that has no teams still has people — the picker it opens to is never the one that
   decides whether the row can be filled in. */
const emptyDraft = () => ({ name: '', kind: 'team', description: '', file: null });

const CompanyEventAwards = () => {
  const { t } = useTranslation(['common', 'awards']);
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/awards`)
      .then(setData)
      .catch(() => navigate(`/host-event/${eventId}`));
  }, [eventId, navigate]);

  useEffect(() => { load(); }, [load]);

  /* Every write answers with the WHOLE screen — the list, the shelf and the candidates — so the
     one place that swaps the state is here. A row that was just revealed must not be re-drawn from
     a guess. */
  const call = (method, path, body) => {
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/event/${eventId}/awards${path}`, body)
      .then((res) => { setData(res); return res; })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  /* The one call that may carry a picture, so the one that may not be JSON. `apiFormRequest` only
     posts, which is why naming a prize and giving it a picture are a POST and never the PUT that
     writes a winner. */
  const save = (fields, file) => {
    setBusy(true);
    setNotice(null);
    const path = `/company/event/${eventId}/awards`;
    const send = () => {
      if (!file) return apiRequest('POST', path, fields);
      const form = new FormData();
      Object.entries(fields).forEach(([k, v]) => {
        if (v !== undefined && v !== null) form.append(k, v);
      });
      form.append('file', file);
      return apiFormRequest(path, form);
    };
    return send()
      .then((res) => { setData(res); return res; })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  if (!data) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{notice || t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const awards = data.awards || [];
  const library = data.library || [];
  const kinds = data.kinds || ['team', 'person', 'company'];
  const canManage = !!data.can_manage;
  const locked = busy;
  const revealed = awards.filter((a) => a.is_announced).length;

  /* Who may win this one. Three lists in the payload, one shape out — the row draws a select and
     does not care which of the three it is looking at. */
  const candidates = (kind) => {
    if (kind === 'person') return (data.people || []).map((p) => ({ id: p.id, name: p.name }));
    if (kind === 'company') {
      return (data.companies || []).map((c) => ({ id: c.id, name: c.name }));
    }
    return (data.teams || []).map((tm) => ({
      id: tm.id,
      // The company beside the team name, because half the teams on a Hercules day are called
      // "Team 3" and a picker of those is unusable.
      name: tm.company_name ? `${tm.name} · ${tm.company_name}` : tm.name,
    }));
  };

  const move = (index, delta) => {
    const next = awards.map((a) => a.id);
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    call('POST', '/reorder', { ids: next }).catch(() => {});
  };

  const setWinner = (row, value) => {
    call('PUT', `/${row.id}`, { winner_kind: row.kind, winner_id: value || '' }).catch(() => {});
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/host-event/${eventId}`} />
        <Title my="1" className="mt-3">
          <Icon name="trophy" theme="primary" className="me-2" />
          {t('awardsTitle')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('awardsHostNote')}</p>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {awards.length > 0 && (
          <div className="hv-counts">
            <div className="hv-count">
              <span className="hv-count__n">{awards.length}</span>
              <span className="hv-count__label">{t('awardsCountPrizes')}</span>
            </div>
            <div className="hv-count">
              <span className="hv-count__n">{awards.filter((a) => a.winner).length}</span>
              <span className="hv-count__label">{t('awardsCountDecided')}</span>
            </div>
            <div className="hv-count">
              <span className="hv-count__n">{revealed}</span>
              <span className="hv-count__label">{t('awardsCountRevealed')}</span>
            </div>
          </div>
        )}

        {awards.length === 0 && <p className="ce-note">{t('awardsNoneYet')}</p>}

        {/* THE RUNNING ORDER */}
        {awards.map((a, index) => {
          const options = candidates(a.kind);
          return (
            <div key={a.id}>
              <div className="bravos-themerow">
                <span
                  className="bravos-themerow__img"
                  style={{ backgroundImage: `url("${awardArt(a.name, a.image)}")`,
                           backgroundSize: 'contain', backgroundRepeat: 'no-repeat' }}
                />
                <span className="bravos-themerow__body">
                  <span className="bravos-themerow__name">
                    {/* Our own 45 have a translated name; a prize a customer typed is shown
                        exactly as they typed it, which is what the defaultValue is for. */}
                    {t(a.name, { ns: 'awards', defaultValue: a.name })}
                  </span>
                  <span className="bravos-themerow__when">
                    <Icon name={KIND_ICONS[a.kind] || 'users'} />
                    {' '}
                    {t(`awardKind_${a.kind}`)}
                    {a.winner ? ` · ${a.winner.name}` : ` · ${t('awardsNoWinnerYet')}`}
                  </span>
                </span>
                {a.is_announced && (
                  <span className="bravos-themerow__live">{t('awardsRevealed')}</span>
                )}
                <button
                  type="button"
                  className="bravos-member__makeadmin"
                  disabled={locked || index === 0}
                  title={t('awardsMoveUp')}
                  onClick={() => move(index, -1)}
                >
                  <Icon name="angle-up" />
                </button>
                <button
                  type="button"
                  className="bravos-member__makeadmin"
                  disabled={locked || index === awards.length - 1}
                  title={t('awardsMoveDown')}
                  onClick={() => move(index, 1)}
                >
                  <Icon name="angle-down" />
                </button>
                <button
                  type="button"
                  className="bravos-member__makeadmin"
                  onClick={() => setEdit(edit === a.id ? null : a.id)}
                >
                  {edit === a.id ? t('close') : t('awardsEdit')}
                </button>
                <button
                  type="button"
                  className="bravos-member__remove"
                  disabled={locked}
                  title={t('awardsTakeOff')}
                  onClick={() => call('DELETE', `/${a.id}`).catch(() => {})}
                >
                  <Icon name="trash" />
                </button>
              </div>

              {/* WHO WON IT, and the reveal. On the row and not behind the expander: this is the
                  only thing anybody does on this screen while the event is running. */}
              <div className="bravos-form__row mb-2">
                <select
                  value={a.winner && a.winner.kind === a.kind ? a.winner.id : ''}
                  disabled={locked}
                  onChange={(e) => setWinner(a, e.target.value)}
                >
                  <option value="">{t('awardsPickWinner')}</option>
                  {options.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className={`bravos-submit ${a.is_announced ? 'bravos-submit--ghost' : ''}`}
                  disabled={locked || (!a.winner && !a.is_announced)}
                  onClick={() => call('PUT', `/${a.id}`, { announced: !a.is_announced })
                    .catch(() => {})}
                >
                  <Icon name={a.is_announced ? 'lock' : 'megaphone'} />
                  {' '}
                  {a.is_announced ? t('awardsHideAgain') : t('awardsReveal')}
                </button>
              </div>
              {/* ★ Said on the row that is about to be pressed, not in a paragraph at the top:
                  revealing is what the room and every phone in it sees. */}
              {!a.is_announced && a.winner && (
                <p className="ct-field__hint">{t('awardsRevealHint')}</p>
              )}
              {options.length === 0 && !a.winner && (
                <p className="ce-note">{t(`awardsNoCandidates_${a.kind}`)}</p>
              )}

              {/* THE PREPARATION: the words under the prize, its picture, what it is for. */}
              {edit === a.id && (
                <div className="bravos-form">
                  <p className="ct-pick__label">{t('awardsForTonight')}</p>
                  <input
                    type="text"
                    defaultValue={a.description || ''}
                    maxLength={500}
                    placeholder={t('awardsDescriptionPlaceholder')}
                    onBlur={(e) => call('PUT', `/${a.id}`, { description: e.target.value })
                      .catch(() => {})}
                  />
                  <p className="ct-field__hint">{t('awardsDescriptionHint')}</p>

                  {/* ★ One of OURS cannot be renamed from here. It is handed out on this event and
                      managed in the back office; a customer renaming "FirstPlace" would rename it
                      on every Hercules day that ever used it. The server refuses it too. */}
                  {a.is_ours ? (
                    <p className="ce-note">{t('awardsOursNote')}</p>
                  ) : (
                    <AwardEditor
                      award={a}
                      kinds={kinds}
                      busy={locked}
                      t={t}
                      onSave={(fields, file) => save({ id: a.award_id, ...fields }, file)
                        .then(() => setEdit(null)).catch(() => {})}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* ADDING ONE — the shelf first, because a company that has run an evening before is one
            tap away from the same prize. */}
        {library.length > 0 && (
          <>
            <p className="ct-pick__label mt-3">{t('awardsFromShelf')}</p>
            <div className="etags etags--pick etags--sm">
              {library.map((a) => (
                <React.Fragment key={a.id}>
                  <button
                    type="button"
                    className="etag etag--btn"
                    disabled={locked}
                    title={t('awardsAddFromShelf')}
                    onClick={() => call('POST', '', { award_id: a.id }).catch(() => {})}
                  >
                    <Icon name="plus" /> {t(a.name, { ns: 'awards', defaultValue: a.name })}
                  </button>
                  {/* ★ Throwing a prize away for good is the ADMIN's, and only while no event
                      anywhere still hands it out — which is why the chip appears on some rows and
                      not others. The server refuses it either way. */}
                  {canManage && a.on_events === 0 && (
                    <button
                      type="button"
                      className="etag etag--btn"
                      disabled={locked}
                      title={t('awardsDeleteForGood', { name: a.name })}
                      onClick={() => call('DELETE', `/shelf/${a.id}`).catch(() => {})}
                    >
                      <Icon name="trash" />
                    </button>
                  )}
                </React.Fragment>
              ))}
            </div>
          </>
        )}

        {data.has_shelf ? (
          draft ? (
            <div className="bravos-form">
              <p className="ct-pick__label">{t('awardsNew')}</p>
              <AwardEditor
                award={{ name: draft.name, kind: draft.kind, image: null }}
                kinds={kinds}
                busy={locked}
                t={t}
                onSave={(fields, file) => save(fields, file)
                  .then(() => setDraft(null)).catch(() => {})}
              />
              <button
                type="button"
                className="bravos-jpic__drop"
                onClick={() => setDraft(null)}
              >
                {t('cancel')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost mt-2"
              disabled={locked}
              onClick={() => setDraft(emptyDraft())}
            >
              <Icon name="plus" /> {t('awardsInventOne')}
            </button>
          )
        ) : (
          <p className="ct-field__hint mt-2">{t('awardsNoShelfNote')}</p>
        )}
      </Card>
    </Base>
  );
};

/* The prize itself: its name, what kind of thing wins it, and its picture. One component for
   inventing one and for correcting one, because they are the same four fields and the same save —
   the only difference is whether an id rides along, which the caller adds. */
const AwardEditor = ({ award, kinds, busy, t, onSave }) => {
  const [name, setName] = useState(award.name || '');
  const [kind, setKind] = useState(award.kind || 'team');
  const [file, setFile] = useState(null);

  return (
    <>
      <input
        type="text"
        value={name}
        maxLength={100}
        placeholder={t('awardsNamePlaceholder')}
        onChange={(e) => setName(e.target.value)}
      />

      <p className="ct-pick__label">{t('awardsWhoCanWin')}</p>
      <div className="ce-tabs">
        {kinds.map((k) => (
          <button
            key={k}
            type="button"
            className={`ce-tab ${kind === k ? 'is-on' : ''}`}
            disabled={busy}
            onClick={() => setKind(k)}
          >
            <Icon name={KIND_ICONS[k] || 'users'} /> {t(`awardKind_${k}`)}
          </button>
        ))}
      </div>
      {/* ★ Changing this only changes which picker the row draws. A winner already named keeps
          reading as what it is, which is why correcting a mistake here is safe. */}
      <p className="ct-field__hint">{t('awardsWhoCanWinHint')}</p>

      <div className="bravos-jpic">
        <span className="bravos-jpic__img">
          <img src={file ? URL.createObjectURL(file) : awardArt(name, award.image)} alt="" />
        </span>
        <span className="bravos-jpic__side">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setFile((e.target.files || [])[0] || null)}
          />
          <span className="bravos-netform__hint">{t('awardsPictureHint')}</span>
        </span>
      </div>

      <button
        type="button"
        className="bravos-submit"
        disabled={busy || !name.trim()}
        onClick={() => onSave({ name: name.trim(), kind }, file)}
      >
        <Icon name="check" /> {t('save')}
      </button>
    </>
  );
};

export default CompanyEventAwards;
